import React from 'react';
import styled from 'styled-components';
import { Session, CommunicationChannel } from '../../models/session'; // Import Session model and CommunicationChannel
import { MessageSenderType } from '../../models/message'; // Import MessageSenderType
import ReactCountryFlag from 'react-country-flag';
import { FaWhatsapp } from 'react-icons/fa'; // Import WhatsApp icon
import { useTheme } from '@mui/material/styles';
import SessionSlaIndicator, { getSlaInfo } from './SessionSlaIndicator';

interface ChatSidebarProps {
    sessions: Session[];
    selectedSession: Session | null; // Changed from selectedSessionId: number | null
    onSelectSession: (session: Session) => void;
    isLoading?: boolean;
    isMobile?: boolean; // Added isMobile prop
}

const SidebarContainer = styled.div<{ $isMobile: boolean; $isDarkMode: boolean }>`
    width: ${props => props.$isMobile ? '100%' : '300px'}; // Full width on mobile
    border-right: ${props => props.$isMobile ? 'none' : `1px solid ${props.$isDarkMode ? '#1E293B' : '#E2E8F0'}`};
    padding: 10px;
    background-color: ${props => props.$isDarkMode ? '#111827' : '#FFFFFF'};
    display: flex;
    flex-direction: column;
    overflow-y: auto;
`;

const Header = styled.h2<{ $isDarkMode: boolean }>`
    margin-top: 0;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid ${props => props.$isDarkMode ? '#1E293B' : '#E2E8F0'};
    font-size: 1.2rem;
    color: ${props => props.$isDarkMode ? '#F1F5F9' : '#0F172A'};
`;

const SearchInput = styled.input<{ $isDarkMode: boolean }>`
    width: calc(100% - 20px); // Adjust width to fit padding
    padding: 8px 10px;
    margin-bottom: 15px;
    border: 1px solid ${props => props.$isDarkMode ? '#1E293B' : '#E2E8F0'};
    border-radius: 8px;
    font-size: 0.9rem;
    box-sizing: border-box;
    background-color: ${props => props.$isDarkMode ? '#1E293B' : '#F8FAFC'};
    color: ${props => props.$isDarkMode ? '#F1F5F9' : '#0F172A'};
`;

interface SessionItemProps {
    selected: boolean;
    $isUnread: boolean; // Changed to transient prop
    $isDarkMode: boolean;
}

const SessionItem = styled.div<SessionItemProps>`
    padding: 15px;
    border-bottom: 1px solid ${props => props.$isDarkMode ? '#1E293B' : '#E2E8F0'};
    cursor: pointer;
    background-color: ${props => {
        if (props.selected) return props.$isDarkMode ? '#1E3A5F' : '#EFF6FF';
        if (props.$isUnread) return props.$isDarkMode ? '#14532D' : '#F0FDF4';
        return props.$isDarkMode ? '#111827' : '#FFFFFF';
    }};
    &:hover {
        background-color: ${props =>
            props.selected
                ? (props.$isDarkMode ? '#1E4A6F' : '#DBEAFE')
                : (props.$isDarkMode ? '#1E293B' : '#F1F5F9')
        };
    }
    /* Ensure constant height to prevent layout shift */
    min-height: 100px; /* Adjust based on your content, or use a more robust method */
    display: flex; /* Using flex to help manage internal spacing */
    flex-direction: column; /* Stack children vertically */
    /* justify-content: center; */ /* Removed to allow content to flow from top */
`;

const SessionItemDetails = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start; /* Align items to the start for multiline last message */
`;

const SessionTextContent = styled.div`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
`;

const UnreadIndicatorPlaceholder = styled.div`
    width: 8px;
    height: 8px;
    margin-top: 4px; /* Space below the time */
    flex-shrink: 0;
`;

const UnreadIndicator = styled(UnreadIndicatorPlaceholder)`
    background-color: #2563EB;
    border-radius: 50%;
    /* Remove margin-left and align-self as it's now in a different flex context */
`;

const SessionIdentifier = styled.div`
    font-weight: bold;
    align-self: flex-start;
    line-height: 1.4;
    margin-bottom: 4px; /* Space before SessionInfo */
`;

const SessionInfo = styled.div<{ $isDarkMode: boolean }>`
    font-size: 0.85em;
    color: ${props => props.$isDarkMode ? '#94A3B8' : '#334155'};
    margin-bottom: 5px;
    display: flex;
    align-items: center;
`;

const LanguageFlag = styled.span`
    margin-right: 5px;
    font-size: 1.2em;
    display: flex;
    align-items: center;
`;

const LastMessagePreview = styled.div<{ $isDarkMode: boolean }>`
    font-size: 0.9em;
    color: ${props => props.$isDarkMode ? '#94A3B8' : '#64748B'};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
    min-height: 1.2em;
`;

const LastMessageSender = styled.span<{ $isDarkMode: boolean }>`
    font-style: italic;
    color: ${props => props.$isDarkMode ? '#94A3B8' : '#64748B'};
    margin-right: 4px;
`;

const TopRightInfo = styled.div`
    display: flex;
    flex-direction: column; /* Stack children vertically */
    align-items: flex-end;  /* Align items to the right (end of the cross axis) */
    flex-shrink: 0; /* Prevent shrinking */
    align-self: flex-start; /* Ensure this block also aligns to start if parent had different align-items */
    gap: 4px; /* Added gap for spacing between items */
`;

const WhatsAppIconStyled = styled.span`
    font-size: 0.9em; 
    color: #25D366; 
    /* margin-left: 5px; */ /* Removed as TopRightInfo is a column */
    line-height: 1; /* Attempt to control line height */
    display: flex; /* To help align icon if it has extra space */
    align-items: center; /* Center icon vertically within its own box */
`;

const LastMessageTime = styled.div<{ $isDarkMode: boolean }>`
    font-size: 0.75em;
    color: ${props => props.$isDarkMode ? '#94A3B8' : '#94A3B8'};
    white-space: nowrap;
`;

const ControlIndicator = styled.span`
    font-size: 0.9em; /* Adjust size as needed */
    line-height: 1;
    display: flex;
    align-items: center;
`;

// Helper to get country code from language code
const getCountryCode = (languageCode: string | undefined | null): string => {
    if (!languageCode) return '';
    const langToCountry: Record<string, string> = {
        EN: 'GB', // Or US for American English flag 🇺🇸
        DE: 'DE',
        NL: 'NL',
        PL: 'PL',
        RU: 'RU',
        HU: 'HU',
        FR: 'FR',
        RO: 'RO',
        ES: 'ES',
    };
    return langToCountry[languageCode.toUpperCase()] || languageCode.toUpperCase();
};

// Helper function for formatting time
const formatTime = (isoString?: string | null): string => {
    if (!isoString) return '';
    try {
        const date = new Date(isoString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return date.toLocaleTimeString(navigator.language, { hour: '2-digit', minute: '2-digit', hour12: false });
        }
        // Show DD/MM for yesterday
        if (date.toDateString() === yesterday.toDateString()) {
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
            return `${day}/${month}`;
        }
        return date.toLocaleDateString(navigator.language, { day: '2-digit', month: '2-digit' });
    } catch (e) {
        return ''; 
    }
};

/**
 * Inline attribution badge for the sidebar row. Returns null when there's
 * nothing meaningful to show (organic / no signal yet). Confidence tiers
 * mirror the backend AttributionConfidence enum — keep colours in sync if
 * new tiers are added.
 */
const renderAttributionChip = (session: Session): React.ReactNode => {
    const { referralLinkId, attributionConfidence, ctwaClid } = session;
    if (!referralLinkId && !ctwaClid) return null;

    let label = '';
    let bg = 'rgba(0,0,0,0.04)';
    let color = 'inherit';
    let title = '';

    switch (attributionConfidence) {
        case 'DIRECT_AD':
            label = '📌 Meta Ad';
            bg = 'rgba(13, 71, 161, 0.12)';
            color = '#0D47A1';
            title = 'Attributed via Click-to-WhatsApp Ad metadata (highest confidence)';
            break;
        case 'DIRECT':
            label = '📌 #REF';
            bg = 'rgba(46, 125, 50, 0.12)';
            color = '#2E7D32';
            title = 'Attributed via #REF code in the candidate\'s first message';
            break;
        case 'BOT_CAPTURED':
            label = '📌 Bot-captured';
            bg = 'rgba(2, 136, 209, 0.12)';
            color = '#0277BD';
            title = 'Attributed via the bot fallback question';
            break;
        case 'MANUAL':
            label = '📌 Manual';
            bg = 'rgba(85, 85, 85, 0.12)';
            color = '#555';
            title = 'Attributed manually by an admin';
            break;
        default:
            // NONE or missing — but ctwa_clid present means "captured but unmapped"
            if (ctwaClid) {
                label = '🟡 Unmapped ad';
                bg = 'rgba(237, 108, 2, 0.14)';
                color = '#ED6C02';
                title = `CTWA clid captured but no admin mapping exists yet (${ctwaClid})`;
                break;
            }
            return null;
    }

    return (
        <div
            title={title}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                marginTop: 4,
                padding: '2px 8px',
                fontSize: 11,
                fontWeight: 600,
                borderRadius: 10,
                background: bg,
                color,
                width: 'fit-content',
                maxWidth: '100%',
            }}
        >
            {label}
        </div>
    );
};

const ChatSidebar: React.FC<ChatSidebarProps> = ({ sessions, selectedSession, onSelectSession, isLoading, isMobile }) => {
    const [searchTerm, setSearchTerm] = React.useState("");
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    if (isLoading) {
        return <SidebarContainer $isMobile={isMobile} $isDarkMode={isDarkMode}>Loading chats...</SidebarContainer>;
    }

    // Helper function to get display name for a session
    const getSessionDisplayName = (session: Session): string => {
        if (session.workerFirstName && session.workerLastName) {
            return `${session.workerFirstName} ${session.workerLastName}`;
        } else if (session.workerFirstName) {
            return session.workerFirstName;
        } else if (session.workerLastName) {
            return session.workerLastName;
        } else if (session.phoneNumber) {
            return session.phoneNumber;
        } else {
            return session.workerId;
        }
    };

    // One clock for the whole list so SLA ordering + chips are consistent per render.
    // Re-runs on every render, so socket-driven list updates refresh it naturally.
    const now = Date.now();
    const recency = (s: Session) => new Date(s.lastMessageAt || s.updatedAt || s.createdAt).getTime();

    // Default order: WhatsApp conversations with an OPEN 24h window first, soonest
    // to expire at the top (work these before they lock out). Everything else
    // (expired windows, non-WhatsApp) sinks below, keeping the prior recency order.
    const sortedSessions = [...sessions].sort((a, b) => {
        const aOpen = a.channel === CommunicationChannel.WHATSAPP && !getSlaInfo(a.lastInboundAt, now).expired;
        const bOpen = b.channel === CommunicationChannel.WHATSAPP && !getSlaInfo(b.lastInboundAt, now).expired;
        if (aOpen && bOpen) {
            return getSlaInfo(a.lastInboundAt, now).msRemaining - getSlaInfo(b.lastInboundAt, now).msRemaining;
        }
        if (aOpen !== bOpen) return aOpen ? -1 : 1;
        return recency(b) - recency(a);
    });

    const filteredSessions = sortedSessions.filter(session => {
        const term = searchTerm.toLowerCase();
        const displayName = getSessionDisplayName(session).toLowerCase();
        return (
            displayName.includes(term) ||
            (session.workerId && session.workerId.toLowerCase().includes(term)) ||
            (session.language && session.language.toLowerCase().includes(term)) ||
            (session.professionKey && session.professionKey.toLowerCase().includes(term))
        );
    });

    return (
        <SidebarContainer $isMobile={isMobile} $isDarkMode={isDarkMode}>
            <Header $isDarkMode={isDarkMode}>Chats</Header>
            <SearchInput 
                type="text"
                placeholder="Search by name, Worker ID, Lang, Prof..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                $isDarkMode={isDarkMode}
            />
            {filteredSessions.map(session => {
                const isUnreadFromData = session.lastMessageAt && 
                                 (!session.adminLastViewedAt || new Date(session.lastMessageAt) > new Date(session.adminLastViewedAt));
                
                // Determine if the item should get unread styling (not selected and actually unread)
                const applyUnreadStyle = !!(isUnreadFromData && !(selectedSession?.id === session.id));

                return (
                    <SessionItem
                        key={session.id}
                        selected={selectedSession?.id === session.id}
                        $isUnread={applyUnreadStyle} // Changed to transient prop
                        $isDarkMode={isDarkMode}
                        onClick={() => onSelectSession(session)}
                    >
                        <SessionItemDetails>
                            <SessionTextContent>
                                <SessionIdentifier>{getSessionDisplayName(session)}</SessionIdentifier>
                                <SessionInfo $isDarkMode={isDarkMode}>
                                    <LanguageFlag>
                                        <ReactCountryFlag
                                            countryCode={getCountryCode(session.language)}
                                            svg
                                            style={{ width: '1.5em', height: '1.5em' }}
                                            title={session.language}
                                        />
                                    </LanguageFlag>
                                    <span>{session.language || 'N/A'}</span>
                                    {session.professionKey && <span>&nbsp;|&nbsp;Prof: {session.professionKey}</span>}
                                </SessionInfo>
                                {renderAttributionChip(session)}
                                {session.lastMessage ? (
                                    <LastMessagePreview $isDarkMode={isDarkMode}>
                                        <LastMessageSender $isDarkMode={isDarkMode}>
                                            {session.lastMessageSender === MessageSenderType.USER ? "User: " :
                                             session.lastMessageSender === MessageSenderType.AI ? "AI: " :
                                             session.lastMessageSender === MessageSenderType.ADMIN ? "Admin: " :
                                             session.lastMessageSender === MessageSenderType.SYSTEM ? "System: " : ""}
                                        </LastMessageSender>
                                        {session.lastMessage}
                                    </LastMessagePreview>
                                ) : (
                                    <LastMessagePreview $isDarkMode={isDarkMode}>
                                        <LastMessageSender $isDarkMode={isDarkMode}>No messages yet.</LastMessageSender>
                                    </LastMessagePreview>
                                )}
                            </SessionTextContent>
                            <TopRightInfo>
                                {session.channel === CommunicationChannel.WHATSAPP && (
                                    <WhatsAppIconStyled title="WhatsApp Channel">
                                        <FaWhatsapp />
                                    </WhatsAppIconStyled>
                                )}
                                {session.channel === CommunicationChannel.WHATSAPP && (
                                    <SessionSlaIndicator
                                        lastInboundAt={session.lastInboundAt}
                                        now={now}
                                        isDarkMode={isDarkMode}
                                    />
                                )}
                                {session.lastMessageAt && <LastMessageTime $isDarkMode={isDarkMode}>{formatTime(session.lastMessageAt)}</LastMessageTime>}
                                {isUnreadFromData ? <UnreadIndicator title="New messages" /> : <UnreadIndicatorPlaceholder />}
                                <ControlIndicator title={session.aiPaused ? "Admin Controlled" : "AI Controlled"}>
                                    {session.aiPaused ? '🧑' : '🤖'}
                                </ControlIndicator>
                            </TopRightInfo>
                        </SessionItemDetails>
                    </SessionItem>
                );
            })}
        </SidebarContainer>
    );
};

export default ChatSidebar;
